// WizardHost — generic wrapper that takes a CHG-1241 manifest + adapter
// output and renders FormDialog wizard mode (CHG-1233).
//
// Owns: per-wizard `values` state, onChange, onSubmit, error display.
// FormDialog owns: step state, navigation, focus/escape, ARIA chrome.
//
// Per-wizard components (CampaignCreateWizard etc.) are thin consumers:
//   <WizardHost manifest={campaignCreateManifest}
//               open={open} onClose={close}
//               onSubmitRequest={async (values) => {
//                 const r = await apiClient().post("/campaigns", values);
//                 navigate(`/campaigns/${r.data.id}`);
//               }}
//   />

import { useCallback, useMemo, useState } from "react";
import { FormDialog } from "../components/composition/FormDialog";
import { adaptManifest, validateStep } from "./manifestAdapter";
import { registerWizardPrimitives } from "./registerWizardPrimitives";
import type { WizardManifest } from "./manifestTypes";

// Register the pending-primitive + custom-render extensions on first import.
registerWizardPrimitives();

export interface WizardHostProps {
  manifest: WizardManifest;
  open: boolean;
  onClose: () => void;
  /** Called when the user confirms the final step. Should perform the API
   *  call + side effects (redirect, toast) + then call onClose() OR throw
   *  to surface an error on the active step. */
  onSubmitRequest: (values: Record<string, unknown>) => Promise<void>;
}

export function WizardHost({ manifest, open, onClose, onSubmitRequest }: WizardHostProps) {
  const adapted = useMemo(() => adaptManifest(manifest), [manifest]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const onChange = useCallback((name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setError(undefined);
  }, []);

  const onSubmit = useCallback(
    async (submittedValues: Record<string, unknown>) => {
      // Run all step validators on the full values payload before posting.
      for (const step of adapted.steps) {
        const err = validateStep(adapted.validators[step.key], submittedValues);
        if (err) {
          setError(err);
          return;
        }
      }
      setBusy(true);
      setError(undefined);
      try {
        await onSubmitRequest(submittedValues);
        // Reset on success.
        setValues({});
        setBusy(false);
      } catch (e: unknown) {
        setBusy(false);
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg || "Submit failed.");
      }
    },
    [adapted, onSubmitRequest],
  );

  // Reset on close — values shouldn't bleed between opens.
  const handleClose = useCallback(() => {
    setValues({});
    setError(undefined);
    setBusy(false);
    onClose();
  }, [onClose]);

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={manifest.title}
      description={manifest.subtitle}
      type="wizard"
      steps={adapted.steps}
      values={values}
      onChange={onChange}
      onSubmit={onSubmit}
      submitLabel="Create"
      size={adapted.size}
      busy={busy}
      error={error}
    />
  );
}
